import {
  BellRing,
  CheckCircle2,
  FileClock,
  MessageCircle,
  Mic,
  PenLine,
  Send,
  ShieldCheck,
  Sliders,
  Sparkles,
} from "lucide-react";
import { Trans, useTranslation } from "@thyra/landing-i18n";
import { CornerCross, SectionHeader } from "./ui/SectionHeader";

/**
 * Flagship feature section: Human Tasks.
 * Merges "approvals" and "human-in-the-loop tasks" into one story:
 * everything that needs a human lands in a single queue, and nothing
 * external ships without a sign-off.
 */

function ApprovalCard() {
  const { t } = useTranslation("home");

  return (
    <div className="marketing-card marketing-card-hover relative flex flex-col overflow-hidden p-6 lg:col-span-2">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <ShieldCheck className="h-4.5 w-4.5" strokeWidth={2} />
        </span>
        <h3 className="text-lg font-semibold tracking-tight text-card-foreground">
          {t("humanTasks.approval.title")}
        </h3>
      </div>
      <p className="mt-2 max-w-lg text-sm leading-relaxed text-muted-foreground">
        {t("humanTasks.approval.body")}
      </p>

      {/* Email draft mockup */}
      <div className="mt-5 rounded-xl border border-border-subtle bg-background/70 p-4 dark:bg-black/20">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className="font-semibold text-foreground">
              {t("humanTasks.approval.to")}
            </span>
            {/* <wbr> keeps Cloudflare Email Obfuscation from rewriting this
                mock address in prerendered HTML, which broke hydration. */}
            sarah@
            <wbr />
            acme.com
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:text-amber-300">
            {t("humanTasks.approval.status")}
          </span>
        </div>
        <p className="mt-2.5 text-[13px] font-semibold text-foreground">
          {t("humanTasks.approval.subject")}
        </p>
        <p className="mt-1.5 line-clamp-2 text-[12px] leading-relaxed text-muted-foreground">
          {t("humanTasks.approval.preview")}
        </p>
        <div className="mt-3.5 flex flex-wrap items-center gap-2">
          <span className="btn-brand inline-flex h-8 items-center gap-1.5 rounded-lg px-3.5 text-[12px] font-semibold">
            <Send className="h-3.5 w-3.5" strokeWidth={2} />
            {t("humanTasks.approval.actions.approve")}
          </span>
          <span className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-background px-3.5 text-[12px] font-semibold text-foreground dark:bg-white/5">
            <PenLine className="h-3.5 w-3.5" strokeWidth={2} />
            {t("humanTasks.approval.actions.edit")}
          </span>
          <span className="inline-flex h-8 items-center rounded-lg px-2 text-[12px] font-semibold text-muted-foreground">
            {t("humanTasks.approval.actions.decline")}
          </span>
        </div>
      </div>
    </div>
  );
}

function EscalationCard() {
  const { t } = useTranslation("home");

  return (
    <div className="marketing-card marketing-card-hover relative flex flex-col overflow-hidden p-6">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <BellRing className="h-4.5 w-4.5" strokeWidth={2} />
        </span>
        <h3 className="text-lg font-semibold tracking-tight text-card-foreground">
          {t("humanTasks.escalation.title")}
        </h3>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        {t("humanTasks.escalation.body")}
      </p>

      {/* Chat escalation mockup */}
      <div className="mt-5 flex flex-1 flex-col justify-end gap-2.5">
        <div
          className="flex items-start gap-2"
          style={{ animation: "agent-creation-bubble-in 0.35s ease-out both" }}
        >
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Sparkles className="h-3 w-3" strokeWidth={2} />
          </span>
          <div className="rounded-xl rounded-tl-sm border border-border-subtle bg-background/80 px-3 py-2.5 dark:bg-black/20">
            <p className="text-[12px] leading-relaxed text-foreground">
              {t("humanTasks.escalation.message")}
            </p>
          </div>
        </div>
        <div className="ml-8 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 dark:bg-primary/10">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-primary">
            {t("humanTasks.escalation.taskLabel")}
          </p>
          <p className="mt-0.5 text-[12px] font-medium text-foreground">
            {t("humanTasks.escalation.taskTitle")}
          </p>
        </div>
      </div>
    </div>
  );
}

const RULES = [
  { id: "externalEmails", ask: true },
  { id: "purchases", ask: true },
  { id: "calendarHolds", ask: false },
  { id: "internalSummaries", ask: false },
] as const;

function RulesCard() {
  const { t } = useTranslation("home");

  return (
    <div className="marketing-card marketing-card-hover flex flex-col p-6">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Sliders className="h-4.5 w-4.5" strokeWidth={2} />
        </span>
        <h3 className="text-lg font-semibold tracking-tight text-card-foreground">
          {t("humanTasks.rules.title")}
        </h3>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        {t("humanTasks.rules.body")}
      </p>
      <ul className="mt-5 space-y-2">
        {RULES.map(({ id, ask }) => (
          <li
            key={id}
            className="flex items-center justify-between gap-2 rounded-lg border border-border-subtle bg-background/70 px-3 py-2 dark:bg-black/20"
          >
            <span className="text-[12.5px] font-medium text-foreground">
              {t(`humanTasks.rules.items.${id}`)}
            </span>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
                ask
                  ? "bg-primary/10 text-primary"
                  : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  ask ? "bg-primary" : "bg-emerald-500"
                }`}
              />
              {ask
                ? t("humanTasks.rules.modes.ask")
                : t("humanTasks.rules.modes.autonomous")}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const CHANNELS = [
  { id: "whatsapp", Icon: MessageCircle, brand: "WhatsApp" },
  { id: "voice", Icon: Mic },
  { id: "inApp", Icon: Sparkles },
] as const;

function AnywhereCard() {
  const { t } = useTranslation("home");

  return (
    <div className="marketing-card marketing-card-hover flex flex-col p-6">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <MessageCircle className="h-4.5 w-4.5" strokeWidth={2} />
        </span>
        <h3 className="text-lg font-semibold tracking-tight text-card-foreground">
          {t("humanTasks.anywhere.title")}
        </h3>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        {t("humanTasks.anywhere.body")}
      </p>
      <div className="mt-5 flex flex-1 flex-col justify-end gap-2">
        {CHANNELS.map((channel) => (
          <div
            key={channel.id}
            className="flex items-center gap-2.5 rounded-lg border border-border-subtle bg-background/70 px-3 py-2 dark:bg-black/20"
          >
            <channel.Icon
              className="h-4 w-4 shrink-0 text-primary"
              strokeWidth={2}
            />
            <span className="text-[12px] font-semibold text-foreground">
              {"brand" in channel
                ? channel.brand
                : t(`humanTasks.anywhere.channels.${channel.id}.label`)}
            </span>
            <span className="min-w-0 truncate text-[11.5px] text-muted-foreground">
              {t(`humanTasks.anywhere.channels.${channel.id}.detail`)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const TRAIL = [
  { id: "drafted", time: "07:12", accent: false },
  { id: "escalated", time: "07:12", accent: false },
  { id: "approved", time: "08:03", accent: false },
  { id: "sent", time: "08:03", accent: true },
] as const;

function AuditTrailCard() {
  const { t } = useTranslation("home");

  return (
    <div className="marketing-card marketing-card-hover flex flex-col p-6">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <FileClock className="h-4.5 w-4.5" strokeWidth={2} />
        </span>
        <h3 className="text-lg font-semibold tracking-tight text-card-foreground">
          {t("humanTasks.trail.title")}
        </h3>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        {t("humanTasks.trail.body")}
      </p>
      <ol className="mt-5 space-y-0">
        {TRAIL.map(({ id, time, accent }, i) => (
          <li key={id} className="relative flex gap-3 pb-4 last:pb-0">
            {i < TRAIL.length - 1 ? (
              <span
                className="absolute left-[7px] top-5 h-full w-px bg-border"
                aria-hidden
              />
            ) : null}
            <span
              className={`relative mt-1 flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-full ${
                accent
                  ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300"
                  : "bg-primary/10 text-primary"
              }`}
            >
              <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} />
            </span>
            <div className="flex min-w-0 flex-1 items-baseline justify-between gap-2">
              <span className="text-[12.5px] font-medium text-foreground">
                {t(`humanTasks.trail.steps.${id}`)}
              </span>
              <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                {time}
              </span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function HumanTasksSection() {
  const { t } = useTranslation("home");

  return (
    <section id="human-tasks" className="relative w-full py-20 sm:py-28">
      <div
        className="bg-grid-lines pointer-events-none absolute inset-0"
        aria-hidden
      />
      <div className="relative mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="relative border-y border-border/70 py-12 sm:py-16">
          <CornerCross className="-left-2 -top-2" />
          <CornerCross className="-right-2 -top-2" />
          <CornerCross className="-bottom-2 -left-2" />
          <CornerCross className="-bottom-2 -right-2" />

          <SectionHeader
            eyebrow={t("humanTasks.eyebrow")}
            title={
              <Trans
                t={t}
                i18nKey="humanTasks.title"
                components={{ br: <br className="hidden sm:block" /> }}
              />
            }
            description={t("humanTasks.description")}
          />

          <div className="mt-12 grid grid-cols-1 gap-4 sm:mt-14 lg:grid-cols-3 lg:gap-5">
            <ApprovalCard />
            <EscalationCard />
            <RulesCard />
            <AnywhereCard />
            <AuditTrailCard />
          </div>
        </div>
      </div>
    </section>
  );
}
